import { useContext, useRef, useState } from "react";
import { css } from "@emotion/react";
import Info from "@mui/icons-material/Info";
import {
  Box,
  Checkbox,
  Chip,
  Stack,
  TableBody,
  TableCell,
  TableRow,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useNavigate } from "react-router-dom";

import { dayjs, NhsNumberSchema } from "@thalamos/common";
import { triggerDownload } from "@thalamos/frontend";
import type {
  ExtendedThalamosUser,
  ExtendedWorkItem,
  WorkItemAction,
  WorkItemMiniForm,
} from "@thalamos/aspire-common";
import {
  applicationTemplateIds,
  getBaseFormTemplate,
  getHeadingOrSubtitle,
  isCqcUserSession,
  isSoadUserSession,
} from "@thalamos/aspire-common";

import { api } from "../../api.js";
import { routeFns } from "../../routePaths.js";
import { ConfirmationModal } from "../ConfirmationModal.js";
import {
  getCompletedApplication,
  userIsRedoAuthorForTeam,
} from "../FormProgressPage/helpers/formContextLogic.js";
import { isAmendForOtherUser } from "../FormProgressPage/helpers/workItemHelpers.js";
import {
  AmendConfirmationDialog,
  FormPdfView,
} from "../FormProgressPage/soad-assessment/SoadAssessmentFormPage.js";
import { WorkItemStatusBox } from "./WorkItemStatusBox.js";
import type { MenuOptionsType } from "~/components/design-system/index.js";
import {
  Banner,
  BannerList,
  Button,
  calculateWorkItemStatus,
  MenuFlyout,
  PopupDialog,
  PopupDialogTitle,
  renderErrorToast,
} from "~/components/design-system/index.js";
import { TooltipInfoBox } from "~/components/design-system/TooltipInfoBox.js";
import { LoggedInUserContext } from "~/Contexts.js";

export function generateSoadFilename(
  workItem: ExtendedWorkItem,
  excludeExtension?: boolean,
): string {
  const soadReportForm = workItem.forms.find(
    (form) => form.formTemplate.id === "soad-report-form",
  );

  if (!soadReportForm) {
    return "form.pdf";
  }

  const hasBeenAmended = soadReportForm.latestVersion > 1;

  const formattedUpdatedDate = dayjs(soadReportForm.updated).format("YYYYMMDD");

  const soadUserName = (
    soadReportForm.versions[0]?.signatures[0]?.userName ?? ""
  ).replace(/\s+/g, "");

  const otherWorkItems = workItem.otherWorkItems.filter(
    (wi) => wi.team.type !== "cqc" && wi.team.type !== "soad",
  );

  const assignedTeamEmail = otherWorkItems[0]?.team.primaryEmail;

  const fileName = `${formattedUpdatedDate}${hasBeenAmended ? "Amended" : ""}SOADPatientReports${soadUserName}${workItem.officialReferenceId ?? ""}${assignedTeamEmail ?? ""}${excludeExtension ? "" : ".pdf"}`;

  return fileName;
}

function sortSoadForms(forms: WorkItemMiniForm[]) {
  const soadReportForm = forms.find(
    (f) => f.formTemplate.id === "soad-report-form",
  );
  const completedCertificates = forms.filter(
    (f) =>
      f.formTemplate.id !== "soad-report-form" &&
      ["complete", "in-progress"].includes(f.status),
  );

  return soadReportForm
    ? [soadReportForm, ...completedCertificates]
    : completedCertificates;
}

function buildWorkItemActions(
  workItem: ExtendedWorkItem & { actions: WorkItemAction[] },
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  navigate: (path: string) => void,
  pathname: string,
  handleArchiveClick: () => void,
  handleDeleteAssessmentClick: () => void,
  handleDeleteClick: () => void,
  handleCancelClick: () => void,
  handleAmendClick: () => void,
  handleViewPdfClick: () => void,
  user: ExtendedThalamosUser,
): MenuOptionsType[] {
  const formContextId = workItem.formContextId!;
  const patientId = workItem.patient.id;

  const showView = !pathname.startsWith(
    routeFns.formContextPage(formContextId, patientId),
  );

  return [
    ...workItem.actions
      .filter((action) => !("formId" in action))
      .filter((action) => !(action.type === "view" && !showView))
      .map((action) => {
        switch (action.type) {
          case "view":
            return {
              icon: "view",
              link: routeFns.formContextPage(formContextId, patientId),
              name: isSoadUserSession(user) ? "Enter Assessment" : "Open",
              disabled: false,
            };

          case "view-patient":
            if (isSoadUserSession(user)) return null;
            if (workItem.status !== "pending") {
              return {
                icon: "person",
                link: routeFns.patientHome(patientId),
                name: "View Patient",
                disabled: false,
              };
            }
            return null;
          case "accept":
            if (isAmendForOtherUser(workItem, user.id)) {
              return {
                icon: "view",
                link: routeFns.formContextPage(formContextId, patientId),
                name: "Open",
                disabled: false,
              };
            }
            return {
              icon: "accept",
              link: routeFns.formReview(formContextId, patientId),
              name: "Review Form & Accept/Decline",
              disabled: false,
            };

          case "claim":
            if (isSoadUserSession(user)) return null;
            return {
              icon: "accept",
              onClick: async () => {
                await api.work.claim(workItem.id);
              },
              name: "Claim",
              disabled: false,
            };

          case "un-claim":
            if (isSoadUserSession(user)) return null;
            return {
              icon: "transfer",
              onClick: async () => {
                await api.work.unClaim(workItem.id);
              },
              name: "Un-Claim",
              disabled: false,
            };

          case "archive":
            return {
              icon: "transfer",
              onClick: async () => {
                if (workItem.status !== "completed")
                  return handleArchiveClick();
                await api.work.archive(workItem.id);
              },
              name: "Archive",
              disabled: false,
            };

          case "un-archive":
            return {
              icon: "transfer",
              onClick: async () => {
                await api.work.unArchive(workItem.id);
              },
              name: "Unarchive",
              disabled: false,
            };

          case "delete":
            return {
              icon: "delete",
              onClick: async () => {
                return handleDeleteClick();
              },
              name: "Delete",
              disabled: false,
            };

          case "delete-assessment":
            return {
              icon: "delete",
              onClick: async () => {
                return handleDeleteAssessmentClick();
              },
              name: "Delete Assessment",
              disabled: false,
            };

          case "cancel-assessment":
            return {
              icon: "cancel",
              onClick: async () => {
                return handleCancelClick();
              },
              name: "Cancel Amendment",
              disabled: false,
            };

          case "assign":
            return {
              icon: "transfer",
              link: routeFns.formContextPageSendRequest(
                workItem.formContextId,
                workItem.patient.id,
                {
                  title: "Send assessment to an additional recipient",
                },
              ),
              name: "Send",
              disabled: false,
            };

          case "amend":
            return {
              icon: "amend",
              onClick: handleAmendClick,
              name: "Amend",
              disabled: false,
            };

          case "download-all":
            return {
              icon: "download",
              onClick: async () => {
                const formIds = sortSoadForms(workItem.forms).map((f) => f.id);
                const firstFormId = formIds[0];
                if (firstFormId === undefined) return;
                const result = await api.forms.getPdf(
                  firstFormId,
                  false,
                  formIds.slice(1),
                );
                if (result.status === 200) {
                  const dataUri = `data:application/pdf;base64,${result.data}`;
                  triggerDownload(dataUri, generateSoadFilename(workItem));
                  if (
                    isCqcUserSession(user) &&
                    workItem.status !== "completed"
                  ) {
                    const response = await api.work.archive(workItem.id);
                    if (response.status !== 204) {
                      renderErrorToast({
                        message:
                          "Failed to archive work item after downloading PDF",
                      });
                    }
                  }
                } else {
                  renderErrorToast({
                    message:
                      (result.data as any)?.reason ?? "Failed to download PDF",
                  });
                }
              },
              name: "Download PDF",
              disabled: false,
            };

          case "view-pdf":
            if (!isSoadUserSession(user)) return null;
            return {
              icon: "view",
              onClick: handleViewPdfClick,
              name: "View PDF",
              disabled: false,
            };

          case "unfinalise":
            return {
              icon: "transfer",
              onClick: () => {
                navigate(
                  routeFns.formContextUnfinaliseDialog(
                    formContextId,
                    patientId,
                  ),
                );
              },
              name: "Undo scrutiny",
              disabled: false,
            };

          default:
            return {
              icon: "cancel",
              onClick: async () => {},
              name: "UNIMPLEMENTED (talk to david): " + action.type,
              disabled: false,
            };
        }
      }),
  ].filter((option) => option !== null) as MenuOptionsType[];
}

export const StatusTableCells = ({
  item,
  showNhsNumber,
  isSelected,
  onSelect,
}: {
  item: ExtendedWorkItem & { actions: WorkItemAction[] };
  showNhsNumber: boolean;
  isSelected: boolean;
  onSelect: () => void;
}) => {
  const userContext = useContext(LoggedInUserContext);
  const user = userContext?.user!;
  const navigate = useNavigate();
  const theme = useTheme();
  const { pathname } = useLocation();
  const [showPopup, setShowPopup] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showDeleteAssessmentModal, setShowDeleteAssessmentModal] =
    useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showAmendPopup, setShowAmendPopup] = useState(false);
  const [showPdf, setShowPdf] = useState(false);

  const nhsNumber = item.patient.nhsNumber ?? "";

  const isSoad = isSoadUserSession(user);
  const isCqc = isCqcUserSession(user);
  const isSoadOrCqc = isSoad || isCqc;

  const handleArchiveClick = () => setShowPopup(true);
  const handleDeleteClick = () => setShowDeleteModal(true);
  const handleDeleteAssessmentClick = () => setShowDeleteAssessmentModal(true);
  const handleCancelClick = () => setShowCancelModal(true);
  const handleAmendClick = () => setShowAmendPopup(true);
  const handleViewPdfClick = () => setShowPdf(true);

  const standaloneForm =
    item?.formContextType === "standalone" ? item.forms[0] : undefined;
  const mainFormTemplate =
    standaloneForm === undefined
      ? null
      : getBaseFormTemplate(
          standaloneForm.formTemplate.id,
          standaloneForm.formTemplate.version,
        );

  const policeIncidentType =
    item.formContextType === "police-incident"
      ? item.forms.find((f) => f.formTemplate.id === "mha-136")
        ? "s136"
        : "mca"
      : null;

  const options = buildWorkItemActions(
    item,
    navigate,
    pathname,
    handleArchiveClick,
    handleDeleteAssessmentClick,
    handleDeleteClick,
    handleCancelClick,
    handleAmendClick,
    handleViewPdfClick,
    user,
  );

  const applicationAdmissionForm = item.forms.find(
    (f) =>
      ["complete", "finalised"].includes(f.status) &&
      applicationTemplateIds.includes(f.formTemplate.id),
  );

  const applicationTemplate = applicationAdmissionForm
    ? getBaseFormTemplate(
        applicationAdmissionForm.formTemplate.id,
        applicationAdmissionForm.formTemplate.version,
      )
    : null;

  const admissionHasCompletedApplication = getCompletedApplication(item.forms);

  const subtitle: string = getHeadingOrSubtitle({
    formContextType: item.formContextType,
    forms: item.forms,
    mainFormTemplate,
    applicationTemplate,
    isHeading: false,
    admissionHasCompletedApplication: !!admissionHasCompletedApplication,
    policeIncidentType,
  });

  const { t } = useTranslation();
  const [isMainMenuOpen, setIsMainMenuOpen] = useState(false);

  const cardRef = useRef(null);

  const formattedDob = item.patient.dateOfBirth
    ? dayjs(item.patient.dateOfBirth).format("DD/MM/YYYY")
    : t("common.unknown");

  const summary = calculateWorkItemStatus(
    theme,
    {
      ...item,
      formContextStatus: item.formContextStatus,
      closedByUserId: item.closedByUserId,
      closedByTeamId: item.closedByTeamId,
      closedReason: item.closedReason,
    },
    user,
  );
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const isNotCompleteStatus = item.status !== "completed";

  const isDeceasedPatient = ["formal", "informal"].includes(
    item.patient.deathNotificationStatus ?? "",
  );

  const pendingLink = isAmendForOtherUser(item, user.id)
    ? routeFns.formContextPage(item.formContextId, item.patient.id)
    : routeFns.formReview(item.formContextId, item.patient.id);

  // A redo this user is responsible for actioning is responded to on its
  // full-page accept/decline preview, so link straight there. Keyed on redo
  // authorship rather than who the work item is assigned to: the admission's
  // claim moves around freely, and the author stays responsible for responding
  // wherever it currently sits.
  const isRedoForThisUser =
    item.requestType === "redo" &&
    item.forms.some(
      (f) =>
        f.id === item.formId &&
        f.redoState === "redo-requested" &&
        userIsRedoAuthorForTeam(f, user.id, user.sessionContext?.teamId),
    );
  const redoReviewLink = isRedoForThisUser
    ? routeFns.formRedoReview(item.formContextId, item.patient.id)
    : undefined;

  return (
    <>
      <TableBody>
        <TableRow>
          <TableCell
            align="left"
            sx={{
              width: "10%",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
              }}
            >
              <Link
                style={{
                  textDecoration: "none",
                }}
                to={
                  // Don't let the user navigate to the patient home page if the work item is pending
                  item.status === "pending"
                    ? pendingLink
                    : redoReviewLink
                      ? redoReviewLink
                      : isSoad
                        ? routeFns.formContextPage(
                            item.formContextId,
                            item.patient.id,
                          )
                        : routeFns.patientHome(item.patient.id)
                }
              >
                <Typography
                  sx={{
                    textDecoration: "none",
                    fontSize: "15px",
                    backgroundColor: "transparent",
                    outline: "none",
                    color: "blue",
                  }}
                  variant="subtitle1"
                >
                  {item.patient.name.family}, {item.patient.name.given}
                </Typography>
              </Link>
              {isDeceasedPatient && (
                <TooltipInfoBox
                  title={
                    "This patient has been marked as deceased in NHS national systems"
                  }
                  icon={<Info sx={{ fontSize: "1.5rem", ml: 1 }} />}
                />
              )}
            </Box>
          </TableCell>
          <TableCell
            align="left"
            sx={{
              width: "10%",
            }}
          >
            <Typography variant="subtitle2">
              <Chip label={formattedDob} size="small" />
            </Typography>
          </TableCell>
          {showNhsNumber && (
            <>
              <TableCell
                align="left"
                sx={{
                  width: "20%",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <Typography variant="subtitle1">
                    {NhsNumberSchema.catch(t("common.unknown")).parse(
                      nhsNumber,
                    )}
                  </Typography>
                </Box>
              </TableCell>
            </>
          )}
          {isSoadOrCqc && (
            <TableCell
              align="left"
              sx={{
                width: "10%",
              }}
            >
              <Link
                style={{
                  textDecoration: "none",
                }}
                to={routeFns.formContextPage(
                  item.formContextId,
                  item.patient.id,
                )}
              >
                <Typography
                  sx={{
                    textDecoration: "none",
                    fontSize: "15px",
                    backgroundColor: "transparent",
                    outline: "none",
                    color: "blue",
                  }}
                  variant="subtitle1"
                >
                  {item.officialReferenceId}
                </Typography>
              </Link>
            </TableCell>
          )}
          {!isSoadOrCqc && (
            <TableCell
              align="left"
              sx={{
                width: "40%",
              }}
            >
              <Link
                style={{
                  textDecoration: "none",
                }}
                to={
                  // Don't let the user navigate to the patient home page if the work item is pending
                  item.status === "pending"
                    ? pendingLink
                    : routeFns.formContextPage(
                        item.formContextId,
                        item.patient.id,
                      )
                }
              >
                <Typography
                  sx={{
                    textDecoration: "none",
                    fontSize: "15px",
                    backgroundColor: "transparent",
                    outline: "none",
                    color: "blue",
                  }}
                  variant="subtitle1"
                >
                  {subtitle}
                </Typography>
              </Link>
            </TableCell>
          )}

          <TableCell
            align="left"
            sx={{
              width: "20%",
            }}
          >
            <Stack sx={{ gap: 1 }}>
              <WorkItemStatusBox
                color={summary.textColour ?? theme.palette.common.black}
                backgroundColor={summary.colour}
                headingText={"Summary"}
                mainText={
                  isMobile && summary.shortStatusText
                    ? summary.shortStatusText
                    : summary.statusText
                }
                align={"flex-start"}
                hideHeadingText={true}
              />
              {summary.secondaryPill && (
                <WorkItemStatusBox
                  color={summary.secondaryPill.textColour}
                  backgroundColor={summary.secondaryPill.colour}
                  headingText={""}
                  mainText={
                    isMobile
                      ? summary.secondaryPill.shortText
                      : summary.secondaryPill.text
                  }
                  align={"flex-start"}
                  hideHeadingText={true}
                />
              )}
            </Stack>
          </TableCell>
          {isCqc && (
            <TableCell
              align="left"
              sx={{
                width: "40%",
              }}
            >
              <WorkItemStatusBox
                key={item.createdBy.userName}
                color={theme.palette.text.primary}
                backgroundColor={theme.palette.statuses.positive}
                headingText={t("components.workItemCard.claimedStatus")}
                mainText={item.createdBy.userName}
                hideHeadingText={true}
              />
            </TableCell>
          )}
          {isCqc && (
            <TableCell
              align="left"
              sx={{
                width: "40%",
              }}
            >
              <Stack
                sx={{
                  gap: 1,
                }}
              >
                {item.otherWorkItems
                  .filter((item) => item.team.type !== "soad")
                  .filter((item) => item.team.type !== "cqc")
                  .map(
                    (item) => item.assignedUserEmail ?? item.team.primaryEmail,
                  )
                  .filter((email): email is string => (email ?? "").length > 0)
                  .map((email) => (
                    <WorkItemStatusBox
                      key={email}
                      color={theme.palette.text.primary}
                      backgroundColor={theme.palette.statuses.positive}
                      headingText={t("components.workItemCard.claimedStatus")}
                      mainText={email}
                      hideHeadingText={true}
                    />
                  ))}
              </Stack>
            </TableCell>
          )}
          {!isSoadOrCqc && (
            <TableCell
              align="left"
              sx={{
                width: "40%",
              }}
            >
              <Box>
                {isNotCompleteStatus &&
                  (item.assignedUserId ? (
                    <WorkItemStatusBox
                      color={theme.palette.text.primary}
                      backgroundColor={theme.palette.statuses?.positive}
                      headingText={t("components.workItemCard.claimedStatus")}
                      mainText={item.assignedUser!.name}
                      hideHeadingText={true}
                    />
                  ) : (
                    <WorkItemStatusBox
                      color={theme.palette.common.black}
                      backgroundColor={theme.palette.statuses.pending}
                      headingText={t("components.workItemCard.claimedByStatus")}
                      mainText={t(
                        "components.workItemCard.notYetClaimedStatus",
                      )}
                      hideHeadingText={true}
                    />
                  ))}
              </Box>
            </TableCell>
          )}
          {isCqc && (
            <TableCell padding="checkbox">
              <Checkbox checked={isSelected} onChange={onSelect} />
            </TableCell>
          )}
          <TableCell
            align="center"
            sx={{
              width: "10%",
            }}
          >
            <Box ref={cardRef} onClick={() => setIsMainMenuOpen(true)}>
              <Flyout
                setIsMainMenuOpen={setIsMainMenuOpen}
                cardRef={cardRef}
                isMainMenuOpen={isMainMenuOpen}
                options={options}
              />
            </Box>
          </TableCell>
        </TableRow>
      </TableBody>
      <PopupDialog open={showPopup} onClose={() => setShowPopup(false)}>
        <Box sx={{ p: 4 }}>
          <PopupDialogTitle
            titleText={t("pages.caseload.popupDialog.popArchiveTitle")}
            closeDialog={() => setShowPopup(false)}
          />
          <Banner
            bannerType={BannerList.INFO}
            title={t("pages.caseload.popupDialog.popupWarningText")}
          />
          <Box
            sx={{
              width: "100%",
              display: "flex",
              justifyContent: "space-between",
              mt: 4,
            }}
          >
            <Button
              variant="outlined"
              label={t("buttonLabels.close")}
              onClick={() => setShowPopup(false)}
            />
            <Button
              label={t("buttonLabels.archive")}
              onClick={async () => {
                await api.work.archive(item.id);
                setShowPopup(false);
              }}
            />
          </Box>
        </Box>
      </PopupDialog>
      {showDeleteAssessmentModal && (
        <ConfirmationModal
          message={t("components.confirmDeleteAssessmentModal.message")}
          confirmFn={() => {
            const deleteAssessmentClick = async () => {
              try {
                const result = await api.soad.delete(item.formContextId);
                if (result.status === 204) {
                  setShowDeleteAssessmentModal(false);
                }
              } catch (e) {
                setShowDeleteAssessmentModal(false);
                renderErrorToast({
                  message:
                    (e as any)?.response?.data?.reason ?? (e as any).message,
                });
              }
            };
            void deleteAssessmentClick();
          }}
          closeFn={() => setShowDeleteAssessmentModal(false)}
        />
      )}
      {showDeleteModal && (
        <ConfirmationModal
          message={t("components.confirmDeleteModal.message")}
          confirmFn={() => {
            const deleteClick = async () => {
              try {
                const result = await api.admissions.delete(item.formContextId);
                if (result.status === 204) {
                  setShowDeleteModal(false);
                } else {
                  renderErrorToast({
                    message:
                      (result.data as any)?.reason ??
                      "Something went wrong while deleting the admission. Please try again later.",
                  });
                }
              } catch {
                setShowDeleteModal(false);
                renderErrorToast({
                  message:
                    "Something went wrong while deleting the admission. Please try again later.",
                });
              }
            };
            void deleteClick();
          }}
          closeFn={() => setShowDeleteModal(false)}
        />
      )}
      {showCancelModal && (
        <ConfirmationModal
          message={t("components.confirmCancelAssessmentModal.message")}
          confirmFn={() => {
            const cancelClick = async () => {
              try {
                const result = await api.soad.cancelAmendment(
                  item.formContextId,
                );
                if (result.status === 204) {
                  setShowCancelModal(false);
                }
              } catch (e) {
                setShowCancelModal(false);
                renderErrorToast({
                  message:
                    (e as any)?.response?.data?.reason ?? (e as any).message,
                });
              }
            };
            void cancelClick();
          }}
          closeFn={() => setShowCancelModal(false)}
        />
      )}
      <AmendConfirmationDialog
        payload={{}}
        open={showAmendPopup}
        onClose={async (result) => {
          setShowAmendPopup(false);

          if (result.confirmed) {
            try {
              if (result.reason) {
                const res = await api.soad.startAmendment(item.formContextId, {
                  reason: result.reason,
                  reasonDescription: result.otherReason,
                });

                if (res.status === 204) {
                  navigate(
                    routeFns.formContextPage(
                      item.formContextId,
                      item.patient.id,
                    ),
                  );
                }
              }
            } catch (e) {
              renderErrorToast({
                message:
                  (e as any)?.response?.data?.message ?? (e as any).message,
              });
            }
          }
        }}
      />
      {showPdf && (
        <FormPdfView
          formContextId={item.formContextId}
          forms={sortSoadForms(item.forms).map((f) => ({
            id: f.id,
            formTemplate: f.formTemplate,
            latestVersion: f.latestVersion,
            // A work item carries no per-version detail, so each form is shown
            // as one document covering every version.
            versions: [],
          }))}
          onClose={() => setShowPdf(false)}
        />
      )}
    </>
  );
};

function Flyout({
  setIsMainMenuOpen,
  cardRef,
  isMainMenuOpen,
  options,
}: {
  setIsMainMenuOpen: (params: boolean) => void;
  cardRef: any;
  isMainMenuOpen?: boolean;
  options: MenuOptionsType[];
}) {
  return (
    <Box
      sx={{
        display: "flex",
      }}
    >
      <Box
        css={css`
          display: flex;
        `}
        onClick={(e) => {
          e.stopPropagation();
          setIsMainMenuOpen(!isMainMenuOpen);
        }}
      >
        {!!options?.length && (
          <MenuFlyout
            cardRef={cardRef}
            options={options}
            isOpen={isMainMenuOpen}
            onClose={() => setIsMainMenuOpen(false)}
          />
        )}
      </Box>
    </Box>
  );
}
